import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { isValidProjectId } from '../../utils/projectIds';
import useAIStore from '../../stores/aiStore';
import RealtimeVoiceChat from '../AI/RealtimeVoiceChat';
import './AccessibleStudioPage.css';

export default function AccessibleStudioPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;
  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const {
    bootstrapChats,
    activeChatId,
    selectedPersona,
    loadMessages,
  } = useAIStore();

  useEffect(() => {
    if (safeProjectId) {
      void bootstrapChats(safeProjectId);
    }
  }, [bootstrapChats, safeProjectId]);

  if (!safeProjectId) {
    return (
      <main className="accessible-studio accessible-studio-loading">
        {t('common.invalidProjectLink', { defaultValue: 'Invalid project link.' })}
      </main>
    );
  }

  if (project === undefined) {
    return (
      <main className="accessible-studio accessible-studio-loading">
        {t('accessibleStudio.loading', { defaultValue: 'Loading Accessible Studio...' })}
      </main>
    );
  }

  if (!project) {
    return (
      <main className="accessible-studio accessible-studio-loading">
        {t('common.projectNotFound', { defaultValue: 'Project not found.' })}
      </main>
    );
  }

  return (
    <main className="accessible-studio">
      <header className="acs-header">
        <button
          type="button"
          className="acs-back"
          onClick={() => navigate(`/projects/${safeProjectId}`)}
        >
          {i18n.dir() === 'rtl' ? '→' : '←'} {t('projects.projectHome', { defaultValue: 'Project Home' })}
        </button>
        <div className="acs-header-copy">
          <p className="acs-kicker">{t('accessibleStudio.kicker', { defaultValue: 'Voice-first writing support' })}</p>
          <h1>{t('accessibleStudio.title', { defaultValue: 'Accessible Studio' })}</h1>
          <p>
            {t('accessibleStudio.pageDescription', {
              defaultValue: 'Use a large-control, transcript-first mentor workspace for live voice guidance while you write.',
            })}
          </p>
        </div>
        <div className="acs-header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(`/projects/${safeProjectId}/editor`)}
          >
            {t('accessibleStudio.openWritingStudio', { defaultValue: 'Open Writing Studio' })}
          </button>
        </div>
      </header>

      <section className="acs-summary-grid">
        <div className="acs-summary-card">
          <span>{t('accessibleStudio.cards.project', { defaultValue: 'Project' })}</span>
          <strong>{project.title}</strong>
        </div>
        <div className="acs-summary-card">
          <span>{t('accessibleStudio.cards.genre', { defaultValue: 'Genre' })}</span>
          <strong>{project.genre || t('common.none', { defaultValue: 'None' })}</strong>
        </div>
        <div className="acs-summary-card">
          <span>{t('accessibleStudio.cards.chatHistory', { defaultValue: 'Chat history' })}</span>
          <strong>{activeChatId ? t('accessibleStudio.cards.connected', { defaultValue: 'Connected' }) : t('common.loading', { defaultValue: 'Loading' })}</strong>
        </div>
      </section>

      <RealtimeVoiceChat
        projectId={safeProjectId}
        chatId={activeChatId}
        personaId={selectedPersona}
        onTranscriptSaved={() => {
          if (activeChatId) {
            void loadMessages(activeChatId);
          }
        }}
      />
    </main>
  );
}
